import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, Plus, Trash2, Edit2, Tag, Calendar, Hash } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PromoCodesAdmin = () => {
  const { toast } = useToast();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    expiryDate: '',
    usageLimit: null,
    description: ''
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/promo-codes`);
      setPromoCodes(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch promo codes',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        discountValue: parseFloat(formData.discountValue),
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        expiryDate: formData.expiryDate || null
      };

      await axios.post(`${API_URL}/api/admin/promo-codes`, payload);
      
      toast({
        title: 'Success',
        description: 'Promo code created successfully'
      });

      setFormData({
        code: '',
        discountType: 'percentage',
        discountValue: 0,
        expiryDate: '',
        usageLimit: null,
        description: ''
      });
      setShowForm(false);
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create promo code',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/promo-codes/${codeId}`);
      toast({
        title: 'Success',
        description: 'Promo code deleted successfully'
      });
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete promo code',
        variant: 'destructive'
      });
    }
  };

  const toggleActive = async (codeId, currentStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/promo-codes/${codeId}`, {
        isActive: !currentStatus
      });
      toast({
        title: 'Success',
        description: `Promo code ${!currentStatus ? 'activated' : 'deactivated'}`
      });
      fetchPromoCodes();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update promo code',
        variant: 'destructive'
      });
    }
  };

  if (loading && promoCodes.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Promo Codes</h2>
          <p className="text-gray-600">Manage subscription discount codes</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Promo Code
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Promo Code</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="WELCOME50"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Discount Type *</Label>
                  <Select
                    value={formData.discountType}
                    onValueChange={(value) => setFormData({ ...formData, discountType: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                      <SelectItem value="free">Free Subscription</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.discountType !== 'free' && (
                  <div>
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.discountValue}
                      onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                      placeholder={formData.discountType === 'percentage' ? '50' : '100'}
                      required
                      className="mt-1"
                    />
                  </div>
                )}

                <div>
                  <Label>Usage Limit (Optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.usageLimit || ''}
                    onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                    placeholder="Unlimited"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Expiry Date (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Internal note about this promo"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-orange-600 hover:bg-orange-700" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Promo Code'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Promo Codes List */}
      <div className="grid grid-cols-1 gap-4">
        {promoCodes.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Tag className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No promo codes yet. Create one to get started!</p>
            </CardContent>
          </Card>
        ) : (
          promoCodes.map((promo) => {
            const isExpired = promo.expiryDate && new Date(promo.expiryDate) < new Date();
            const limitReached = promo.usageLimit && promo.usedCount >= promo.usageLimit;

            return (
              <Card key={promo.id} className={!promo.isActive || isExpired || limitReached ? 'opacity-60' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{promo.code}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          promo.isActive && !isExpired && !limitReached
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {isExpired ? 'Expired' : limitReached ? 'Limit Reached' : promo.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Discount</p>
                          <p className="font-semibold text-gray-900">
                            {promo.discountType === 'free' ? 'FREE' :
                             promo.discountType === 'percentage' ? `${promo.discountValue}%` :
                             `$${promo.discountValue}`}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Used</p>
                          <p className="font-semibold text-gray-900">
                            {promo.usedCount} {promo.usageLimit ? `/ ${promo.usageLimit}` : ''}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Expires</p>
                          <p className="font-semibold text-gray-900">
                            {promo.expiryDate
                              ? new Date(promo.expiryDate).toLocaleDateString()
                              : 'Never'}
                          </p>
                        </div>

                        {promo.description && (
                          <div className="md:col-span-1">
                            <p className="text-sm text-gray-600">Note</p>
                            <p className="text-sm text-gray-900">{promo.description}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleActive(promo.id, promo.isActive)}
                        disabled={isExpired || limitReached}
                      >
                        {promo.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(promo.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PromoCodesAdmin;
